import { init, type IVWOClient } from "vwo-fme-node-sdk";

import { config } from "@/config";

/**
 * VWO ships the browser FME SDK and the server SDK from the same npm package (`vwo-fme-node-sdk`).
 * Vite client builds apply the `browser` export condition, so this import resolves to
 * `dist/client/vwo-fme-javascript-sdk.min.js`, not the Node server bundle (see package.json `exports`
 * and https://developers.vwo.com/v2/docs/fme-javascript-install).
 */
let clientPromise: Promise<IVWOClient | null> | null = null;

type VwoFmeInitParams = {
  accountId: number;
  sdkKey: string;
  proxyUrl: string;
};

function readVwoFmeInitParams(): VwoFmeInitParams | null {
  const cfg = config.getCachedConfig();
  if (!cfg?.isVWOEnabled) {
    return null;
  }
  const accountId = Number(cfg.vwoAccountId ?? 0);
  const sdkKey = String(cfg.vwoSDKKey ?? "").trim();
  const proxyUrl = String(cfg.vwoProxyUrl ?? "").trim();
  if (accountId <= 0 || !sdkKey || !proxyUrl) {
    return null;
  }
  return { accountId, sdkKey, proxyUrl };
}

export function resetVwoFmeClientForTesting(): void {
  clientPromise = null;
}

/**
 * Lazily initializes a single VWO FME client for the app. Returns null when VWO is off or misconfigured.
 */
export function getVwoFmeClient(): Promise<IVWOClient | null> {
  const initParams = readVwoFmeInitParams();
  if (!initParams) {
    return Promise.resolve(null);
  }
  if (!clientPromise) {
    clientPromise = (async () => {
      try {
        return await init({
          sdkKey: initParams.sdkKey,
          accountId: String(initParams.accountId),
          proxyUrl: initParams.proxyUrl,
        });
      } catch (error) {
        console.error("[VWO FME] init failed:", error);
        clientPromise = null;
        return null;
      }
    })();
  }
  return clientPromise;
}
